import { proxy, subscribe } from 'valtio';

type Theme = 'light' | 'dark' | 'system';

interface ThemeState {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
}

const getInitialTheme = (): Theme => {
  if (typeof window === 'undefined') return 'system';
  return (localStorage.getItem('nivaas-theme') as Theme) || 'system';
};

const getResolvedTheme = (theme: Theme): 'light' | 'dark' => {
  if (theme === 'system') {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return theme;
};

export const themeState = proxy<ThemeState>({
  theme: 'system',
  resolvedTheme: 'light',
});

export const initializeTheme = () => {
  const theme = getInitialTheme();
  themeState.theme = theme;
  themeState.resolvedTheme = getResolvedTheme(theme);
  applyTheme(themeState.resolvedTheme);

  // Listen for system theme changes
  if (typeof window !== 'undefined') {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      if (themeState.theme === 'system') {
        themeState.resolvedTheme = e.matches ? 'dark' : 'light';
        applyTheme(themeState.resolvedTheme);
      }
    });
  }
};

export const setTheme = (theme: Theme) => {
  themeState.theme = theme;
  themeState.resolvedTheme = getResolvedTheme(theme);
  localStorage.setItem('nivaas-theme', theme);
  applyTheme(themeState.resolvedTheme);
};

const applyTheme = (resolvedTheme: 'light' | 'dark') => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(resolvedTheme);
};

// Subscribe to state changes
if (typeof window !== 'undefined') {
  subscribe(themeState, () => {
    applyTheme(themeState.resolvedTheme);
  });
}
